"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ScanRequestSchema = exports.ScanResultSchema = exports.RenderSignalSchema = exports.RENDER_SIGNALS = exports.ScoreSetSchema = exports.PageSnapshotSchema = exports.CheckItemSchema = exports.CheckGroupSchema = exports.CheckStatusSchema = void 0;
exports.isNoindex = isNoindex;
exports.normalizeUrlKey = normalizeUrlKey;
exports.problemsOf = problemsOf;
const zod_1 = require("zod");
const tier_1 = require("./tier");
/**
 * Kết quả Source Read + kiểm tra kỹ thuật — PRS §5, §8.
 *
 * Cấu trúc bám theo mockup của BA (`docs/ai-seo-copilot-admin-demo_20260803.html`):
 * mỗi mục kiểm tra trả về trạng thái ok/warn/bad kèm ghi chú, và được xếp vào
 * một trong ba nhóm hiển thị. Khác với cách chỉ liệt kê lỗi: người dùng thấy
 * được cả những mục đang đạt, không phải đoán.
 */
/** Trạng thái một mục kiểm tra — dùng đúng ba mức của mockup. */
exports.CheckStatusSchema = zod_1.z.enum(['ok', 'warn', 'bad']);
/** Nhóm hiển thị, tương ứng các tab trong mockup. */
exports.CheckGroupSchema = zod_1.z.enum(['technical', 'content', 'invariant']);
exports.CheckItemSchema = zod_1.z.object({
    /** Mã ổn định để đối chiếu giữa các lần quét, vd `canonical` */
    code: zod_1.z.string(),
    group: exports.CheckGroupSchema,
    status: exports.CheckStatusSchema,
    /** Tên ngắn hiện ở cột đầu, vd "Meta Title" */
    label: zod_1.z.string(),
    /** Câu giải thích cho người không rành SEO đọc */
    note: zod_1.z.string(),
    /** Giá trị hiện tại, để hiển thị đối chiếu */
    actual: zod_1.z.string().nullable(),
    /** Tier xử lý theo PRS §9 — null khi mục đang đạt */
    tier: tier_1.TierSchema.nullable(),
    /** Số điểm bị trừ khi mục này không đạt */
    weight: zod_1.z.number().int().min(0),
    /**
     * Hệ thống có sinh được nội dung sửa cho mục này không.
     * Dùng để tính điểm dự kiến sau khi áp dụng — mục không sửa được thì vẫn trừ.
     */
    fixable: zod_1.z.boolean(),
});
/** Ảnh chụp các thành phần đọc được từ HTML — PRS §5.1, §5.2 */
exports.PageSnapshotSchema = zod_1.z.object({
    url: zod_1.z.string().url(),
    fetchedAt: zod_1.z.coerce.date(),
    httpStatus: zod_1.z.number().int(),
    /** Thời gian tải, ms — đầu vào thô cho phần Performance ở §5.3 */
    responseTimeMs: zod_1.z.number().int(),
    htmlBytes: zod_1.z.number().int(),
    // §5.1 HTML Analysis
    title: zod_1.z.string().nullable(),
    metaDescription: zod_1.z.string().nullable(),
    canonical: zod_1.z.string().nullable(),
    robotsMeta: zod_1.z.string().nullable(),
    lang: zod_1.z.string().nullable(),
    hasViewportMeta: zod_1.z.boolean(),
    openGraph: zod_1.z.record(zod_1.z.string()),
    twitterCard: zod_1.z.record(zod_1.z.string()),
    headings: zod_1.z.object({
        h1: zod_1.z.array(zod_1.z.string()),
        h2: zod_1.z.array(zod_1.z.string()),
        h3: zod_1.z.array(zod_1.z.string()),
    }),
    jsonLdTypes: zod_1.z.array(zod_1.z.string()),
    // §5.2 Content Analysis
    wordCount: zod_1.z.number().int(),
    imageCount: zod_1.z.number().int(),
    imagesMissingAlt: zod_1.z.number().int(),
    /**
     * Đường dẫn của các ảnh thiếu ALT, tối đa 20 cái.
     * Cần để đề xuất được mô tả theo tên file (spec SC-02 §3.2) — chỉ có con số
     * thì người dùng biết thiếu mà không biết thiếu ở ảnh nào.
     * `.default([])` vì các lần quét lưu trước spec SC-02 không có trường này.
     */
    imagesMissingAltSrc: zod_1.z.array(zod_1.z.string()).default([]),
    internalLinks: zod_1.z.number().int(),
    externalLinks: zod_1.z.number().int(),
    /**
     * Đích của các liên kết nội bộ, đã chuẩn hoá về `host/pathname` — spec SC-17 §5.2.
     * Tối đa 300; `.default([])` vì các lần quét lưu trước spec SC-17 không có.
     */
    internalHrefs: zod_1.z.array(zod_1.z.string()).default([]),
    /** Trang có khối câu hỏi thường gặp hay không */
    hasFaq: zod_1.z.boolean(),
    /**
     * §5.4 Site Invariant. Site đặt `data-vi` và `data-en` trên HAI phần tử anh em
     * (mỗi ngôn ngữ một phần tử), KHÔNG phải hai thuộc tính trên cùng một node —
     * đã kiểm chứng trên HTML thật 2026-08-06. Vì vậy đối chiếu bằng SỐ LƯỢNG.
     */
    bilingual: zod_1.z.object({
        viNodes: zod_1.z.number().int(),
        enNodes: zod_1.z.number().int(),
        /** Chênh lệch tuyệt đối giữa hai bên; 0 là khớp */
        mismatch: zod_1.z.number().int(),
    }),
    hasContactWidget: zod_1.z.boolean(),
    ctaHrefs: zod_1.z.array(zod_1.z.string()),
    /**
     * §SC-15 — dấu hiệu trang dựng bằng JavaScript.
     *
     * Ba trường này có `.default()` chứ không bắt buộc: các lần quét lưu trước
     * spec SC-15 nằm sẵn trong cột `Scan.detail` dạng JSON và không có chúng.
     * Bắt buộc là làm hỏng việc đọc lại lịch sử điểm cũ.
     */
    scriptCount: zod_1.z.number().int().default(0),
    /** Có `#root` / `#app` / `#__next` / `[data-reactroot]` mà bên trong gần như trống */
    hasEmptyAppRoot: zod_1.z.boolean().default(false),
    /** Có `<noscript>` với nội dung yêu cầu bật JavaScript */
    noscriptRequiresJs: zod_1.z.boolean().default(false),
});
/** Bộ ba điểm số — PRS §12 Executive Summary */
exports.ScoreSetSchema = zod_1.z.object({
    seo: zod_1.z.number().int().min(0).max(100),
    technical: zod_1.z.number().int().min(0).max(100),
    content: zod_1.z.number().int().min(0).max(100),
});
/**
 * Dấu hiệu nghi trang dựng bằng JavaScript — spec SC-15 §3.1.
 * Mã ổn định để test và để chỉnh ngưỡng; nhãn để hiện cho người dùng.
 */
exports.RENDER_SIGNALS = {
    'thin-vs-bytes': 'Rất ít chữ so với dung lượng HTML nhận được',
    'no-heading': 'Không có thẻ tiêu đề H1, H2 hay H3 nào',
    'empty-app-root': 'Có khung ứng dụng rỗng (#root / #app / #__next)',
    'script-heavy': 'Nhiều thẻ script mà rất ít nội dung',
    'noscript-notice': 'Trang có thông báo yêu cầu bật JavaScript',
};
exports.RenderSignalSchema = zod_1.z.enum(Object.keys(exports.RENDER_SIGNALS));
exports.ScanResultSchema = zod_1.z.object({
    snapshot: exports.PageSnapshotSchema,
    /** Toàn bộ mục kiểm tra, kể cả mục đang đạt */
    checks: zod_1.z.array(exports.CheckItemSchema),
    scores: exports.ScoreSetSchema,
    /** Điểm ước tính nếu áp dụng hết các đề xuất sửa được */
    potential: exports.ScoreSetSchema,
    /**
     * Nghi trang dựng bằng JavaScript → điểm không đáng tin (spec SC-15).
     * `.default(false)` vì các lần quét cũ trong DB chưa có trường này.
     */
    renderWarning: zod_1.z.boolean().default(false),
    renderSignals: zod_1.z.array(exports.RenderSignalSchema).default([]),
});
exports.ScanRequestSchema = zod_1.z.object({
    url: zod_1.z.string().url('URL không hợp lệ'),
});
/**
 * Trang có tự chặn lập chỉ mục không — spec SC-17 §4 Y6.
 *
 * `meta robots` là danh sách chỉ thị ngăn bằng dấu phẩy (`noindex, nofollow`), và
 * `none` là cách viết tắt của `noindex, nofollow`. Bắt cả hai; tìm chuỗi con thì
 * dính nhầm cả những giá trị chỉ chứa chữ đó trong một từ dài hơn.
 */
function isNoindex(robotsMeta) {
    if (!robotsMeta)
        return false;
    return robotsMeta
        .toLowerCase()
        .split(',')
        .map((s) => s.trim())
        .some((d) => d === 'noindex' || d === 'none');
}
/**
 * Chuẩn hoá một địa chỉ TUYỆT ĐỐI để so khớp giữa trang và liên kết — spec SC-17 §5.3.
 *
 * Trả về `host + đường dẫn`: bỏ giao thức, bỏ `www.`, bỏ cổng mặc định, bỏ `/` ở
 * cuối, bỏ query và fragment. Cùng một hàm phải dùng cho CẢ `publicUrl` của trang
 * LẪN `href` bắt được — nếu không thì `https://x.vn/products/rooland` và
 * `https://www.x.vn/products/rooland/` không khớp nhau, đúng cái bẫy mà `addPage`
 * đã gặp một lần và phải xử lý bằng `urlVariants`.
 *
 * KHÔNG dùng `URL`, và KHÔNG nhận địa chỉ tương đối: gói `shared` cố ý biên dịch
 * với `lib: ["ES2023"]`, không có DOM — cùng lý do đã ghi ở `hostOf` trong
 * `invariant.ts`. Bên gọi tự ghép địa chỉ tương đối với gốc trước khi truyền vào
 * (tầng API có `URL` của Node).
 *
 * Đường dẫn GIỮ NGUYÊN chữ hoa/thường vì phần lớn máy chủ phân biệt; chỉ tên miền
 * mới hạ chữ thường. Cổng khác mặc định thì giữ lại — hai cổng khác nhau là hai
 * nơi khác nhau, gộp chúng lại là báo trùng giả.
 *
 * Trả null nếu không phải http(s) — người viết HTML gõ sai địa chỉ là chuyện có thật.
 */
function normalizeUrlKey(raw) {
    const m = /^(https?):\/\/(?:[^@/?#]*@)?([^/?#]+)([^?#]*)/i.exec(raw.trim());
    if (!m)
        return null;
    const scheme = m[1].toLowerCase();
    const congMacDinh = scheme === 'https' ? ':443' : ':80';
    const host = m[2]
        .toLowerCase()
        .replace(new RegExp(`${congMacDinh}$`), '')
        .replace(/^www\./, '');
    // Cắt gạch chéo cuối bằng vòng lặp, không bằng `/\/+$/` — biểu thức đó có độ
    // phức tạp siêu tuyến tính trên chuỗi toàn gạch chéo, cùng lý do đã tránh ở
    // `uniqueSlug` của registry.
    let path = m[3] ?? '';
    while (path.endsWith('/'))
        path = path.slice(0, -1);
    return `${host}${path}`;
}
/** Các mục chưa đạt — thứ hiện ở danh sách "Vấn đề phát hiện". */
function problemsOf(checks) {
    return checks.filter((c) => c.status !== 'ok');
}
//# sourceMappingURL=analysis.js.map